import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Lets a customer swap the size of an existing cart line without returning
 * to the product page.
 *
 * Direct adaptation of cart-line-color-swap.js - same underlying mechanism,
 * same already-solved bugs, only the option being swapped differs (Taille
 * instead of Couleur). See that file for the full history; kept in sync
 * deliberately rather than sharing a single generic implementation, so each
 * stays simple to read on its own and a future change to one doesn't have to
 * reason about the other's option-specific assumptions.
 *
 * No native "swap the variant of an existing line" endpoint exists, so this
 * is implemented as add-then-remove: the new variant is added FIRST via
 * /cart/add.js, and the old line is only removed via /cart/change.js after
 * that succeeds. A failed second step therefore never loses the product
 * entirely - worst case is "both sizes briefly in the cart" (clearly
 * surfaced to the customer via #status), never "product vanished".
 *
 * Deliberately reuses the exact same native primitives already used
 * elsewhere in the cart (the quantity selector in component-cart-items.js,
 * the add-to-cart flow in product-form.js, and cart-line-color-swap.js
 * itself): the same endpoints, and the same CartLinesUpdateEvent contract
 * that cart-items-component already listens for globally (document-level
 * 'shopify:cart:lines-update') to refresh every cart surface on the page
 * (drawer, cart page, header bubble). No bespoke DOM-patching/morphing code
 * here at all - resolving the event's promise with the standard {cart,
 * detail: {sections, ...}} shape is enough for the existing listener to do
 * the rest.
 *
 * Known limitation (disclosed, not a bug - inherited from
 * cart-line-color-swap.js, same root cause): the swapped-in line lands at
 * the bottom of the cart (it's a new line), not in the old line's position.
 * Quantity is preserved.
 *
 * All of the following were fixed once already on cart-line-color-swap.js
 * before this file existed - applied here directly rather than re-discovered:
 * - on:click needs an explicit component-tag-name selector
 *   ("cart-line-size-swap-component/selectSize/...") - without one,
 *   component.js's getClosestComponent() resolves to the nearest
 *   "-component" ancestor, which is the nested anchored-popover-component,
 *   not this one, and the click would silently do nothing.
 * - this.refs.popover would silently be undefined for the same nested-
 *   component-scoping reason - walk up from the clicked element instead
 *   (event.target.closest('[popover]')).
 * - POSTing the batch {items, sections} shape to /cart/add.js returns a
 *   leaner payload (no token/total_price/currency/item_count) than
 *   CartLinesUpdateEvent.createCartFromAjaxResponse expects - converting it
 *   directly can throw and skip the removal step entirely. Isolated in
 *   #resolveWithFullCart() below (a separate /cart.json fetch), fire-and-
 *   forget, so a failure there can never block #removeLine().
 * - Resolving the event's promise with cart:null (rather than real data)
 *   would leave native analytics/conversion tracking without the payload it
 *   expects - assets/product-form.js's own add-to-cart flow always resolves
 *   with a real full-cart fetch, never the lean add.js response directly;
 *   #resolveWithFullCart() matches that.
 * - The popover must be positioned with plain getBoundingClientRect() math
 *   (see #positionPopover()), not CSS anchor positioning - never reliably
 *   verifiable without a real browser, and already caused a silent "nothing
 *   happens on click, no error, no network request" failure once.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Hidden live region for success/error messaging.
 *
 * @extends {Component<Refs>}
 */
export class CartLineSizeSwapComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const popover = this.querySelector('[popover]');
    const trigger = this.querySelector('[popovertarget]');
    if (popover instanceof HTMLElement && trigger instanceof HTMLElement) {
      popover.addEventListener('beforetoggle', (event) => {
        if (!(event instanceof ToggleEvent) || event.newState !== 'open') return;
        this.#positionPopover(popover, trigger);
      });
    }
  }

  /**
   * Positions the popover directly below its trigger, clamped to stay within
   * the viewport horizontally. Plain fixed-position math - see class doc.
   * @param {HTMLElement} popover
   * @param {HTMLElement} trigger
   */
  #positionPopover(popover, trigger) {
    const spacing = 6;
    const viewportMargin = 16;
    const maxWidth = 200;

    const rect = trigger.getBoundingClientRect();
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - maxWidth - viewportMargin));

    popover.style.position = 'fixed';
    popover.style.top = `${rect.bottom + spacing}px`;
    popover.style.left = `${left}px`;
  }

  /**
   * Handles a click on one of the alternative size options.
   * @param {number} variantId - The new variant's numeric ID (parsed from the on:click data segment).
   * @param {PointerEvent} event
   */
  selectSize(variantId, event) {
    event.preventDefault();

    if (this.hasAttribute('data-loading')) return;

    const { lineKey, quantity } = this.dataset;
    if (!lineKey || !quantity) return;

    // BUG TROUVE ET CORRIGE (avant tout signalement reel - trouve en
    // construisant le bundle CleanyStep) : cet ajout ne portait jamais les
    // proprietes de la ligne d'origine - inoffensif pour un produit sans
    // proprietes personnalisees, mais aurait silencieusement efface les
    // "Tapis 1/2/3: <couleur>" d'une ligne bundle (X2/X3 Premium -
    // CleanyStep) au moindre changement de taille. data-properties (pose par
    // cart-line-size-swap.liquid, toujours present - {} si la ligne n'a
    // aucune propriete) est repris tel quel.
    /** @type {Record<string, string>} */
    let properties = {};
    try {
      properties = JSON.parse(this.dataset.properties || '{}');
    } catch (error) {
      console.warn('[cart-line-size-swap] data-properties parse failed, continuing without properties', error);
    }

    // Not this.refs.popover - see class doc (nested-component ref scoping).
    if (event.target instanceof Element) {
      const popover = event.target.closest('[popover]');
      if (popover instanceof HTMLElement) popover.hidePopover();
    }
    this.setAttribute('data-loading', '');
    this.#setStatus('');

    const addPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId.toString(), quantity: Number(quantity) }],
        promise: addPromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: Number(quantity), properties }],
          sections: this.#cartSectionIds().join(','),
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        // Shopify's /cart/add.js returns a truthy "status" (e.g. 422) plus a
        // "message" on failure - same check used by the native add-to-cart
        // flow (assets/product-form.js) and cart-line-color-swap.js.
        if (response.status) {
          addPromise.reject(new Error(response.message || 'Add to cart failed'));
          this.#setStatus(response.message || 'Impossible d’ajouter cette taille au panier.');
          return null;
        }

        // Fire-and-forget: resolves addPromise once a full cart fetch comes
        // back, but never awaited here - #removeLine() below must never wait
        // on it, see class-level doc.
        this.#resolveWithFullCart(addPromise, response);

        // Only remove the old line once the new one is confirmed in the cart.
        return this.#removeLine(lineKey);
      })
      .catch((error) => {
        console.error('[cart-line-size-swap]', error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error while swapping size',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.#setStatus('Une erreur réseau est survenue, merci de réessayer.');
      })
      .finally(() => {
        this.removeAttribute('data-loading');
      });
  }

  /**
   * Removes the original line now that the replacement variant has been added.
   * Mirrors component-cart-items.js's onLineItemRemove/updateQuantity pattern
   * (and cart-line-color-swap.js's own #removeLine()), using the line's
   * unique key (robust to position shifts) rather than a 1-based line index.
   * @param {string} lineKey
   * @returns {Promise<void>}
   */
  #removeLine(lineKey) {
    const removePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: lineKey, quantity: 0 }],
        promise: removePromise.promise,
      })
    );

    return fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.#cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          const message = typeof response.errors === 'string' ? response.errors : 'Cart update failed';
          removePromise.reject(new Error(message));
          // The add already succeeded and already refreshed the UI - the
          // customer now sees BOTH sizes in the cart. Surface this clearly
          // rather than silently leaving an unexplained duplicate line.
          this.#setStatus(
            'La nouvelle taille a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
          );
          return;
        }

        this.#resolveWithFullCart(removePromise, response);
      })
      .catch((error) => {
        console.error('[cart-line-size-swap] remove failed after add succeeded', error);
        removePromise.reject(error);
        this.#setStatus(
          'La nouvelle taille a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
        );
      });
  }

  /**
   * Resolves a CartLinesUpdateEvent's promise with cart data built from a
   * FULL /cart.json fetch - not the response of the add.js/change.js call
   * that triggered it, which is missing fields (see class doc). Mirrors
   * assets/product-form.js's #refreshCart() and cart-line-color-swap.js's
   * own #resolveWithFullCart(). Deliberately not awaited by callers: this
   * must never delay or block whatever happens next (e.g. #removeLine() in
   * selectSize() above) - it only resolves a promise other listeners
   * (cart-items-component, potentially Shopify's own analytics) are
   * watching, nothing here depends on its result.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
   * @param {{sections?: unknown, items?: unknown}} addOrChangeResponse
   */
  async #resolveWithFullCart(promiseHandle, addOrChangeResponse) {
    let cart = null;
    try {
      const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
    } catch (error) {
      console.warn('[cart-line-size-swap] full cart fetch/conversion failed, continuing anyway', error);
    }

    promiseHandle.resolve({
      cart,
      detail: {
        sections: addOrChangeResponse.sections,
        items: addOrChangeResponse.items,
        source: 'cart-line-size-swap',
        didError: false,
      },
    });
  }

  /** @returns {string[]} Section IDs of every cart-items-component on the page. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(/** @returns {id is string} */ (id) => Boolean(id));
  }

  /** @param {string} message */
  #setStatus(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
  }
}

if (!customElements.get('cart-line-size-swap-component')) {
  customElements.define('cart-line-size-swap-component', CartLineSizeSwapComponent);
}
